import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  MenuItem,
  Grid,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  BarChart as UsageIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import axios from 'axios';

// Status chip component
const StatusChip = ({ status, isActive }) => {
  if (status === 'loading') {
    return <CircularProgress size={20} />;
  }

  return (
    <Chip
      label={isActive ? 'Active' : 'Inactive'}
      color={isActive ? 'success' : 'error'}
      size="small"
      icon={isActive ? <ActiveIcon /> : <BlockIcon />}
    />
  );
};

// User details dialog component
const UserDetailsDialog = ({ open, onClose, user, onStatusChange, onUserUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    vatNumber: '',
    serviceTier: '',
    isActive: true
  });

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        companyName: user.companyName || '',
        vatNumber: user.vatNumber || '',
        serviceTier: user.serviceTier || 'free',
        isActive: user.isActive
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post(`/api/admin/users/${user._id}/toggle-status`);
      
      if (response.data.status === 'success') {
        const newStatus = !formData.isActive;
        setFormData(prev => ({ ...prev, isActive: newStatus }));
        onStatusChange(user._id, newStatus);
      } else {
        throw new Error(response.data.message || 'Failed to update user status');
      }
    } catch (err) {
      setError(err.message || 'Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.put(`/api/admin/users/${user._id}`, formData);
      
      if (response.data.status === 'success') {
        onUserUpdate(user._id, response.data.data.user);
        onClose();
      } else {
        throw new Error(response.data.message || 'Failed to update user');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PersonIcon />
          <Typography>Edit User</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              margin="normal"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              margin="normal"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              margin="normal"
              size="small"
              required
              type="email"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Company Name"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              margin="normal"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="VAT Number"
              name="vatNumber"
              value={formData.vatNumber}
              onChange={handleInputChange}
              margin="normal"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Service Tier"
              name="serviceTier"
              value={formData.serviceTier}
              onChange={handleInputChange}
              margin="normal"
              size="small"
            >
              <MenuItem value="free">Free</MenuItem>
              <MenuItem value="professional">Professional</MenuItem>
              <MenuItem value="enterprise">Enterprise</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <StatusChip status={loading ? 'loading' : 'ready'} isActive={formData.isActive} />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleStatusChange}
          color={formData.isActive ? "error" : "success"}
          disabled={loading}
        >
          {formData.isActive ? 'Deactivate' : 'Activate'} User
        </Button>
        <Button 
          onClick={handleSave}
          color="primary"
          disabled={loading}
          startIcon={<SaveIcon />}
          variant="contained"
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// User usage dialog component
const UserUsageDialog = ({ open, onClose, userId, username }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usageData, setUsageData] = useState(null);

  useEffect(() => {
    if (open && userId) {
      fetchUsageData();
    }
  }, [open, userId]);

  const fetchUsageData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`/api/admin/users/${userId}/usage`);
      
      if (response.data.status === 'success') {
        setUsageData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch usage data');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch usage data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {console.log('usageData', usageData)}
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <UsageIcon />
          <Typography>Usage Statistics: {username}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : usageData ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                API Usage Summary
              </Typography>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total API Calls
                    </Typography>
                    <Typography variant="h5">
                      {usageData.apiCalls.total.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      This Month
                    </Typography>
                    <Typography variant="h5">
                      {(usageData.apiCalls.thisMonth ?? 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {usageData.limits.usagePercentage.toFixed(1)}% of limit
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Last Month
                    </Typography>
                    <Typography variant="h5">
                      {(usageData.apiCalls.lastMonth ?? 0).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Echo Calls
                    </Typography>
                    <Typography variant="h5">
                      {usageData.apiCalls.echo?.toLocaleString() ?? 0}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                API Keys Usage
              </Typography>
              <Paper elevation={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Key Name</TableCell>
                      <TableCell>Total Calls</TableCell>
                      <TableCell>% of Total</TableCell>
                      <TableCell>Last Used</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usageData.usage.byApiKey.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">No API keys found</TableCell>
                      </TableRow>
                    ) : (
                      usageData.usage.byApiKey.map((key) => (
                        <TableRow key={key.keyId}>
                          <TableCell>{key.name}</TableCell>
                          <TableCell>{key.total.toLocaleString()}</TableCell>
                          <TableCell>{key.percentage.toFixed(1)}%</TableCell>
                          <TableCell>
                            {key.lastUsed ? new Date(key.lastUsed).toLocaleString() : 'Never used'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Monthly Usage Trend
              </Typography>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell>Total Calls</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {console.log('Monthly Trend:', usageData.usage.monthlyTrend)}
                    {usageData.usage.monthlyTrend.map((month, idx) => (
                      <TableRow key={`${month.year}-${month.month}-${idx}`}>
                        <TableCell>{month.label}</TableCell>
                        <TableCell>{month.total.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Plan Details
              </Typography>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current Plan
                    </Typography>
                    <Typography sx={{ textTransform: 'capitalize' }}>
                      {usageData.subscription.tier}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Monthly Limit
                    </Typography>
                    <Typography>
                      {usageData.limits.monthlyLimit.toLocaleString()} calls
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      API Keys
                    </Typography>
                    <Typography>
                      {usageData.limits.apiKeyCount} / {usageData.limits.apiKeyLimit}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Typography>No usage data available</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={fetchUsageData} startIcon={<RefreshIcon />} disabled={loading}>
          Refresh
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function UserManagement() {
  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUsername, setSelectedUsername] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get('/api/admin/users', {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            search: searchQuery
          }
        });

        if (response.data.status === 'success') {
          setUsers(response.data.data.users);
          setTotalUsers(response.data.data.total);
        } else {
          throw new Error(response.data.message || 'Failed to fetch users');
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [page, rowsPerPage, searchQuery, refreshKey]);

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
  };

  const handleStatusChange = (userId, newStatus) => {
    setUsers(users.map(user => 
      user._id === userId 
        ? { ...user, isActive: newStatus }
        : user
    ));
  };

  const handleUserUpdate = (userId, updatedUser) => {
    setUsers(users.map(user => 
      user._id === userId 
        ? { ...user, ...updatedUser }
        : user
    ));
  };

  const handleViewUsage = (userId, username) => {
    setSelectedUserId(userId);
    setSelectedUsername(username);
    setUsageDialogOpen(true);
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <TextField
            placeholder="Search users..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 300 }}
          />
          
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            variant="outlined"
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Service Tier</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {user.serviceTier}
                  </TableCell>
                  <TableCell>{user.companyName || '-'}</TableCell>
                  <TableCell>
                    <StatusChip status="ready" isActive={user.isActive} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleUserClick(user)}
                      title="Edit User"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleViewUsage(user._id, user.username)}
                      title="View Usage"
                    >
                      <UsageIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* User Details Dialog */}
      <UserDetailsDialog
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        user={selectedUser}
        onStatusChange={handleStatusChange}
        onUserUpdate={handleUserUpdate}
      />

      {/* User Usage Dialog */}
      <UserUsageDialog
        open={usageDialogOpen}
        onClose={() => setUsageDialogOpen(false)}
        userId={selectedUserId}
        username={selectedUsername}
      />
    </Box>
  );
}

export default UserManagement; 